import { ROLES } from "@coup/shared";
import { useState } from "react";
import { ICONS, ROLE_ART } from "../../art";
import { Icon } from "../../components/Icon";
import { useSound } from "../../sound";
import styles from "./Table.module.css";

interface Props {
  code: string;
  responseMs: number;
  isHost: boolean;
  onEndMatch: () => void;
  onCollapse: () => void;
}

export function ReferencePanel({ code, responseMs, isHost, onEndMatch, onCollapse }: Props) {
  const { muted, volume, setMuted, setVolume } = useSound();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div className={styles.refHead}>
        <div>
          <div className={styles.refCodeLabel}>{copied ? "Copied" : "Lobby code"}</div>
          <div className={styles.refCode}>{code}</div>
        </div>
        <button
          type="button"
          className={styles.squareButton}
          aria-label="Copy lobby code"
          onClick={copy}
        >
          <Icon path={ICONS.copy} size={18} />
        </button>
      </div>
      <div className={styles.refSmall}>
        Response window: {responseMs / 1000} s · Deck: 3 per character
      </div>
      <div className={styles.rule} />
      <div className={styles.refHeading}>Characters</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {ROLES.map((r) => {
          const art = ROLE_ART[r];
          return (
            <div key={r} className={styles.refRole}>
              <div className={styles.refIcon} style={{ background: art.color }}>
                <Icon path={art.icon} size={18} color="#efe6d2" width={1.8} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{art.name}</div>
                <div className={styles.refAction}>{art.action}</div>
                <div className={styles.refCounter}>{art.counter}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.rule} />
      <div className={styles.refHeading}>Anyone may</div>
      <div className={styles.refGeneral}>
        <div>
          <b>Income</b> <span>— take 1. Can&apos;t be stopped.</span>
        </div>
        <div>
          <b>Foreign Aid</b> <span>— take 2. Duke blocks.</span>
        </div>
        <div>
          <b>Coup</b> <span>— pay 7, a player loses a card.</span>
        </div>
        <div style={{ color: "var(--timer)" }}>
          If you have 10+ coins, you must Coup!
        </div>
      </div>
      <div style={{ flex: 1 }} />
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={volume}
        aria-label="Volume"
        className={styles.volume}
        onChange={(e) => setVolume(Number(e.target.value))}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={styles.squareButton}
          aria-label={muted ? "Unmute sound" : "Mute sound"}
          onClick={() => setMuted(!muted)}
        >
          <Icon path={muted ? ICONS.mute : ICONS.sound} size={18} />
        </button>
        {isHost ? (
          <button type="button" className={styles.endButton} onClick={onEndMatch}>
            Host: end match
          </button>
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <button
          type="button"
          className={styles.squareButton}
          aria-label="Collapse reference panel"
          onClick={onCollapse}
        >
          <Icon path={ICONS.collapseLeft} size={18} />
        </button>
      </div>
    </>
  );
}
